import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { HiOutlineCheckCircle, HiOutlineTrash, HiOutlineXMark } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import SectionHeader from '../../components/SectionHeader';
import FormInput from '../../components/FormInput';
import FormEntry from '../../components/FormEntry';
import Button from '../../components/Button';
import { useCreateRider, useUpdateRider, useDeleteRider } from '../../hooks/useRiders';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { useActionGuard } from '../../components/ActionGuard/useActionGuard';
import Skeleton from 'components/Skeleton';

interface Rider {
  id: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;
  display_name?: string;
  mass?: number;
  time_ref?: number;
  distance_ref?: number;
  speed_ref?: number;
  time_ref_minutes?: number;
  distance_ref_km?: number;
  speed_ref_kmh?: number;
  power_ref?: number;
  [key: string]: any;
}

interface RiderFormProps {
  isNew: boolean;
  rider?: Rider;
  isRiderPending: boolean;
  isRiderFetching: boolean;
}

export default function RiderForm({ isNew, rider, isRiderPending, isRiderFetching }: RiderFormProps): ReactElement {
  const navigate = useNavigate();
  const title = isNew ? 'New Rider' : 'Rider Details';

  const formMethods = useForm<Rider>({
    defaultValues: rider,
  });

  const { getValues, reset } = formMethods;

  const createRider = useCreateRider();
  const updateRider = useUpdateRider(rider?.id || '');
  const deleteRider = useDeleteRider();
  const actionGuard = useActionGuard();

  // Use unsaved changes warning hook
  const { clearUnsavedChanges } = useUnsavedChangesWarning({
    enabled: true,
    message: 'You have unsaved changes to this rider. Are you sure you want to leave?',
    formMethods: formMethods,
  });

  // Reset form when rider data changes
  useEffect(() => {
    if (rider && !isNew) {
      reset(rider);
    }
  }, [rider, isNew, reset]);

  const save = async () => {
    try {
      const formValues = getValues();
      const cleanedValues = Object.fromEntries(
        Object.entries(formValues).filter(([, v]) => v !== '' && v !== undefined),
      );

      if (isNew) {
        const newRider = await createRider.mutateAsync(cleanedValues);
        clearUnsavedChanges();
        toast.success('Rider created successfully');
        navigate(`/riders/${newRider.id}`);
      } else {
        await updateRider.mutateAsync(cleanedValues);
        clearUnsavedChanges();
        toast.success('Rider updated successfully');
      }
    } catch (e) {
      console.error(e);
      toast.error('Unknown error saving rider');
    }
  };

  const handleDelete = async () => {
    if (!rider?.id) return;

    try {
      await deleteRider.mutateAsync(rider.id);
      clearUnsavedChanges();
      toast.success('Rider deleted successfully');
      navigate('/team');
    } catch (e) {
      console.error(e);
      toast.error('Unknown error deleting rider');
    }
  };

  const confirmDelete = () => {
    actionGuard.open({
      message: `Are you sure you want to delete ${rider?.display_name || rider?.nickname || 'this rider'}?\n\nThis action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      actionFn: handleDelete,
    });
  };

  const handleCancel = () => {
    navigate('/team');
  };

  const RiderActionButtons = () => {
    return (
      <div className="cr-flex cr-flex-row cr-items-center cr-justify-between cr-gap-1">
        <div className="cr-flex cr-flex-row cr-gap-1">
          <Button onClick={formMethods.handleSubmit(save)} icon={<HiOutlineCheckCircle className={'cr-size-7'} />}>
            Save
          </Button>
          <Button onClick={handleCancel} icon={<HiOutlineXMark className={'cr-size-7'} />}>
            Cancel
          </Button>
        </div>
        {!isNew && (
          <Button onClick={confirmDelete} icon={<HiOutlineTrash className={'cr-size-7'} />}>
            Delete
          </Button>
        )}
      </div>
    );
  };

  return (
    <FormProvider {...formMethods}>
      <actionGuard.ActionGuard />
      <SectionHeader
        title={title}
        backLink={{ to: '/team', text: 'Back to Team' }}
        isLoading={createRider.isPending || updateRider.isPending || deleteRider.isPending || isRiderFetching}
      />
      <fieldset disabled={createRider.isPending || updateRider.isPending || deleteRider.isPending || isRiderFetching}>
        <div className="cr-flex cr-flex-row cr-gap-4 cr-p-4 cr-font-main cr-text-base cr-text-lg">
          <div className="md:cr-w-[624px]">
            <RiderActionButtons />
          </div>
        </div>
        <Skeleton isPending={isRiderPending}>
          <div className="cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
            <div className="cr-mb-4 cr-border-b cr-border-gray-200 cr-pb-2">
              <h3 className="font-montserrat cr-text-2xl-tight cr-font-semibold cr-uppercase cr-text-blue-900">
                Basic Information
              </h3>
            </div>
            <FormEntry label="First Name">
              <FormInput fieldName="first_name" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Last Name">
              <FormInput fieldName="last_name" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Nickname">
              <FormInput fieldName="nickname" required="Nickname is Required" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Display Name">
              <div className="cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-bg-gray-50 cr-px-3 cr-py-2 cr-text-gray-700">
                {(() => {
                  const nickname = formMethods.watch('nickname');
                  const lastName = formMethods.watch('last_name');
                  const firstName = formMethods.watch('first_name');

                  if (!nickname) return 'Enter nickname to generate display name';

                  const nameParts = [];
                  if (lastName) nameParts.push(lastName.toUpperCase());
                  if (firstName) nameParts.push(firstName);

                  return nameParts.length > 0 ? `${nickname} (${nameParts.join(', ')})` : nickname;
                })()}
              </div>
            </FormEntry>
            <FormEntry label="Mass (kg)">
              <FormInput fieldName="mass" inputType="number" required="Mass is Required" customClass="cr-w-full" />
            </FormEntry>
          </div>
          <div className="cr-mt-4 cr-flex cr-flex-col cr-rounded-lg cr-bg-white cr-p-2 cr-text-center cr-shadow-md md:cr-w-[640px] md:cr-text-left">
            <div className="cr-mb-4 cr-border-b cr-border-gray-200 cr-pb-2">
              <h3 className="font-montserrat cr-text-2xl-tight cr-font-semibold cr-uppercase cr-text-blue-900">
                Reference Values
              </h3>
            </div>
            <div className="cr-mb-4 cr-rounded-lg cr-bg-blue-50 cr-p-3 cr-text-sm cr-text-gray-700">
              <p>Time, Distance, Speed, and Power are used to express aerodynamic drag in seconds, km/h, or Watts</p>
            </div>
            <FormEntry label="Time Reference (minutes)">
              <FormInput fieldName="time_ref_minutes" inputType="number" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Distance Reference (km)">
              <FormInput fieldName="distance_ref_km" inputType="number" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Speed Reference (km/h)">
              <FormInput fieldName="speed_ref_kmh" inputType="number" customClass="cr-w-full" />
            </FormEntry>
            <FormEntry label="Power Reference (watts)">
              <FormInput fieldName="power_ref" inputType="number" customClass="cr-w-full" />
            </FormEntry>
          </div>
          <div className="cr-flex cr-flex-row cr-gap-4 cr-p-4 cr-font-main cr-text-base cr-text-lg">
            <div className="md:cr-w-[640px]">
              <RiderActionButtons />
            </div>
          </div>
        </Skeleton>
      </fieldset>
    </FormProvider>
  );
}
